import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../redux/Slices/busSlice';
import { Link } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';

// Import CSS for react-pdf
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Import assets
import logo from '../../components/assets/logos/logo2.PNG';
import englishGuide from '../assets/giudes/English.pdf';
import hindiGuide from '../assets/giudes/Hindi.pdf';

// Standardized Worker Setup using CDN (Avoids local resolution errors)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const UserGuide = () => {
  const { t } = useTranslation();
  const currentLanguage = useSelector(selectLanguage);
  const [isMobile, setIsMobile] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const getPdfGuide = () => {
    return currentLanguage === 'hi' ? hindiGuide : englishGuide;
  };

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setLoading(false);
    setError(false);
  }

  function onDocumentLoadError(err) {
    console.error('Error loading PDF:', err);
    setLoading(false);
    setError(true);
  }

  const goToPrevPage = () => {
    setPageNumber(prevPage => Math.max(prevPage - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prevPage => Math.min(prevPage + 1, numPages));
  };

  const getScale = () => {
    if (isMobile) {
      return window.innerWidth < 400 ? 0.6 : 0.8;
    }
    return 1.2; // Slightly larger for desktop clarity
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      paddingBottom: '40px',
    },
    header: {
      background: 'linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%)',
      padding: isMobile ? '10px 20px' : '15px 40px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      borderBottom: '3px solid #ffeb3b',
      marginBottom: '20px',
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    logoLink: {
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
    },
    logoImage: {
      height: isMobile ? '45px' : '60px',
      width: 'auto',
      borderRadius: '8px',
    },
    backButton: {
      background: '#ff5722',
      color: 'white',
      border: 'none',
      padding: isMobile ? '8px 15px' : '10px 20px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: 'bold',
      textDecoration: 'none',
    },
    mainContent: {
      padding: isMobile ? '10px' : '20px',
      maxWidth: '1000px',
      margin: '0 auto',
    },
    guideTitle: {
      textAlign: 'center',
      color: '#333',
      marginBottom: '20px',
      fontSize: isMobile ? '1.5rem' : '2.2rem',
      fontWeight: '700',
    },
    pdfContainer: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
      padding: isMobile ? '10px' : '30px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: '600px',
    },
    controls: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '20px',
      marginTop: '25px',
      background: '#f8f9fa',
      padding: '10px 20px',
      borderRadius: '50px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
    },
    navButton: {
      background: '#ff5722',
      color: 'white',
      border: 'none',
      padding: '8px 18px',
      borderRadius: '20px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold',
      transition: 'opacity 0.2s',
      opacity: (props) => props.disabled ? 0.5 : 1,
    },
    pageInfo: {
      fontSize: '14px',
      color: '#444',
      fontWeight: '600',
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '100px 20px',
    },
    spinner: {
      width: '50px',
      height: '50px',
      border: '5px solid #f3f3f3',
      borderTop: '5px solid #ff5722',
      borderRadius: '50%',
      margin: '0 auto',
      animation: 'spin 1s linear infinite',
    },
    errorContainer: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#d32f2f',
    },
    footer: {
      background: '#fff9c4',
      padding: '25px 40px',
      textAlign: 'center',
      marginTop: '50px',
      borderTop: '1px solid #ffeb3b'
    },
  };

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .react-pdf__Page {
        box-shadow: 0 0 8px rgba(0,0,0,0.1);
      }
      .react-pdf__Page__canvas {
        max-width: 100% !important;
        height: auto !important;
      }
      button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <Link to="/dashboard" style={styles.logoLink}>
            <img src={logo} alt="Logo" style={styles.logoImage} />
          </Link>
          <Link to="/" style={styles.backButton}>
            ← {t('backToDashboard', 'Back to Dashboard')}
          </Link>
        </div>
      </header>

      <main style={styles.mainContent}>
        <h1 style={styles.guideTitle}>
          {currentLanguage === 'hi' ? 'उपयोगकर्ता गाइड' : 'User Guide'}
        </h1>
        
        <div style={styles.pdfContainer}>
          {loading && (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <p style={{ marginTop: '20px', fontWeight: '500' }}>
                {currentLanguage === 'hi' ? 'गाइड लोड हो रहा है...' : 'Loading guide...'}
              </p>
            </div>
          )}
          
          {error && (
            <div style={styles.errorContainer}>
              <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                ⚠️ {currentLanguage === 'hi' 
                ? 'गाइड लोड करने में त्रुटि।' 
                : 'Error loading guide.'}
              </p>
              <button onClick={() => window.location.reload()} style={styles.navButton}>
                {currentLanguage === 'hi' ? 'पुनः प्रयास करें' : 'Retry'}
              </button>
            </div>
          )}
          
          <Document
            file={getPdfGuide()}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null} // We handle our own loading UI
          >
            <Page 
              pageNumber={pageNumber} 
              scale={getScale()} 
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </Document>
          
          {numPages && !error && (
            <div style={styles.controls}>
              <button onClick={goToPrevPage} disabled={pageNumber <= 1} style={styles.navButton}>
                {currentLanguage === 'hi' ? 'पिछला' : 'Prev'}
              </button>
              <span style={styles.pageInfo}>
                {pageNumber} / {numPages}
              </span>
              <button onClick={goToNextPage} disabled={pageNumber >= numPages} style={styles.navButton}>
                {currentLanguage === 'hi' ? 'अगला' : 'Next'}
              </button>
            </div>
          )}
        </div>
      </main>

      <footer style={styles.footer}>
        <p>© {new Date().getFullYear()} Rajdoot Explorations. {t('allRightsReserved', 'All rights reserved.')}</p>
      </footer>
    </div>
  );
};

export default UserGuide;